import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Alert,
  AlertDescription,
} from "@openzeppelin/ui-components";
import { Icon } from "@ascend/shared/components";
import {
  DataTable,
  type DataTableColumn,
  PaginationControls,
  SearchBar,
  FilterTabs,
  TABLE_PAGE_SIZE_OPTIONS,
  type SortDirection,
} from "../components/ListControls";
import { CLIENT_CLAIM_HISTORY, CLIENT_SETTLEMENTS } from "../data/mockAdapter";

// ─── Types ────────────────────────────────────────────────────────────────────

type SettlementStatus = "claimable" | "expiring-soon" | "expired";
type SettlementFilter = "all" | SettlementStatus;
type ClaimHistoryStatus = "claimed" | "expired";
type SettlementSortKey =
  | "accountId"
  | "token"
  | "amount"
  | "estimatedValue"
  | "daysRemaining"
  | "status";
type ClaimHistorySortKey =
  | "date"
  | "accountId"
  | "token"
  | "amount"
  | "valueUsd"
  | "status";

interface Settlement {
  id: string;
  accountId: string;
  token: string;
  tokenColor: string;
  amount: number;
  estimatedValue: number;
  claimDeadline: string;
  daysRemaining: number;
  status: SettlementStatus;
}

interface ClaimRecord {
  id: string;
  date: string;
  accountId: string;
  token: string;
  tokenColor: string;
  amount: number;
  valueUsd: number;
  status: ClaimHistoryStatus;
}

const initialSettlements: Settlement[] = CLIENT_SETTLEMENTS;
const CLAIM_HISTORY: ClaimRecord[] = CLIENT_CLAIM_HISTORY;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatAmount(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

function parseDateToTimestamp(value: string): number {
  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) return parsed;
  const [day, month, year] = value.split("/").map((part) => Number(part));
  if (!day || !month || !year) return 0;
  return new Date(year, month - 1, day).getTime();
}

function TokenBadge({
  token,
  colorClass,
}: {
  token: string;
  colorClass: string;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${colorClass}`}
    >
      {token}
    </span>
  );
}

function SettlementStatusBadge({ status }: { status: SettlementStatus }) {
  switch (status) {
    case "claimable":
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-700">
          <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-3 w-3" />
          Claimable
        </span>
      );
    case "expiring-soon":
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700">
          <Icon name="streamline:warning-triangle" className="h-3 w-3" />
          Expiring Soon
        </span>
      );
    case "expired":
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-700">
          <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-3 w-3" />
          Expired
        </span>
      );
  }
}

function ClaimHistoryStatusBadge({ status }: { status: ClaimHistoryStatus }) {
  if (status === "claimed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-700">
        <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-3 w-3" />
        Claimed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-700">
      <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-3 w-3" />
      Expired
    </span>
  );
}

// ─── Helper Components ────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  subtitle,
  icon,
  valueClassName,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">
          {title}
        </span>
        {icon}
      </div>
      <div
        className={`mt-2 text-2xl sm:text-3xl font-bold ${valueClassName ?? ""}`}
      >
        {value}
      </div>
      {subtitle && (
        <div className="mt-1 text-xs sm:text-sm text-muted-foreground">
          {subtitle}
        </div>
      )}
    </div>
  );
}

// ─── Filter Config ────────────────────────────────────────────────────────────

const SETTLEMENT_FILTERS: { value: SettlementFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "claimable", label: "Claimable" },
  { value: "expiring-soon", label: "Expiring Soon" },
  { value: "expired", label: "Expired" },
];

// ─── Page Component ───────────────────────────────────────────────────────────

export function LiquidationClaimPage() {
  const navigate = useNavigate();
  const [settlements, setSettlements] =
    useState<Settlement[]>(initialSettlements);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [isClaimingAll, setIsClaimingAll] = useState(false);

  const [stlSearch, setStlSearch] = useState("");
  const [stlStatusFilter, setStlStatusFilter] =
    useState<SettlementFilter>("all");
  const [stlSortKey, setStlSortKey] =
    useState<SettlementSortKey>("daysRemaining");
  const [stlSortDirection, setStlSortDirection] =
    useState<SortDirection>("asc");
  const [stlPage, setStlPage] = useState(1);
  const [stlPageSize, setStlPageSize] = useState<number>(
    TABLE_PAGE_SIZE_OPTIONS[0],
  );

  const [histSearch, setHistSearch] = useState("");
  const [histSortKey, setHistSortKey] = useState<ClaimHistorySortKey>("date");
  const [histSortDirection, setHistSortDirection] =
    useState<SortDirection>("desc");
  const [histPage, setHistPage] = useState(1);
  const [histPageSize, setHistPageSize] = useState<number>(
    TABLE_PAGE_SIZE_OPTIONS[0],
  );

  const claimableSettlements = settlements.filter(
    (s) => s.status !== "expired",
  );
  const totalClaimableValue = claimableSettlements.reduce(
    (sum, s) => sum + s.estimatedValue,
    0,
  );
  const claimedThisMonth = CLAIM_HISTORY.filter(
    (c) => c.status === "claimed" && c.date >= "2026-03-01",
  ).reduce((sum, c) => sum + c.valueUsd, 0);
  const expiringSoon = settlements.filter(
    (s) => s.status === "expiring-soon",
  ).length;

  const stlStatusCounts = useMemo<Record<SettlementFilter, number>>(
    () => ({
      all: settlements.length,
      claimable: settlements.filter((s) => s.status === "claimable").length,
      "expiring-soon": settlements.filter((s) => s.status === "expiring-soon")
        .length,
      expired: settlements.filter((s) => s.status === "expired").length,
    }),
    [settlements],
  );

  const filteredSettlements = useMemo(() => {
    let items = [...settlements];
    if (stlSearch.trim()) {
      const q = stlSearch.toLowerCase();
      items = items.filter(
        (s) =>
          s.accountId.toLowerCase().includes(q) ||
          s.token.toLowerCase().includes(q),
      );
    }
    if (stlStatusFilter !== "all") {
      items = items.filter((s) => s.status === stlStatusFilter);
    }
    return items;
  }, [settlements, stlSearch, stlStatusFilter]);

  const sortedSettlements = useMemo(() => {
    const items = [...filteredSettlements];
    items.sort((a, b) => {
      let comparison = 0;
      switch (stlSortKey) {
        case "accountId":
          comparison = a.accountId.localeCompare(b.accountId);
          break;
        case "token":
          comparison = a.token.localeCompare(b.token);
          break;
        case "amount":
          comparison = a.amount - b.amount;
          break;
        case "estimatedValue":
          comparison = a.estimatedValue - b.estimatedValue;
          break;
        case "daysRemaining":
          comparison = a.daysRemaining - b.daysRemaining;
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return stlSortDirection === "asc" ? comparison : -comparison;
    });
    return items;
  }, [filteredSettlements, stlSortDirection, stlSortKey]);

  const paginatedSettlements = useMemo(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(sortedSettlements.length / stlPageSize),
    );
    const safePage = Math.min(stlPage, totalPages);
    return sortedSettlements.slice(
      (safePage - 1) * stlPageSize,
      safePage * stlPageSize,
    );
  }, [sortedSettlements, stlPage, stlPageSize]);

  const filteredHistory = useMemo(() => {
    if (!histSearch.trim()) return CLAIM_HISTORY;
    const q = histSearch.toLowerCase();
    return CLAIM_HISTORY.filter(
      (c) =>
        c.accountId.toLowerCase().includes(q) ||
        c.token.toLowerCase().includes(q),
    );
  }, [histSearch]);

  const sortedHistory = useMemo(() => {
    const items = [...filteredHistory];
    items.sort((a, b) => {
      let comparison = 0;
      switch (histSortKey) {
        case "date":
          comparison =
            parseDateToTimestamp(a.date) - parseDateToTimestamp(b.date);
          break;
        case "accountId":
          comparison = a.accountId.localeCompare(b.accountId);
          break;
        case "token":
          comparison = a.token.localeCompare(b.token);
          break;
        case "amount":
          comparison = a.amount - b.amount;
          break;
        case "valueUsd":
          comparison = a.valueUsd - b.valueUsd;
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return histSortDirection === "asc" ? comparison : -comparison;
    });
    return items;
  }, [filteredHistory, histSortDirection, histSortKey]);

  const paginatedHistory = useMemo(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(sortedHistory.length / histPageSize),
    );
    const safePage = Math.min(histPage, totalPages);
    return sortedHistory.slice(
      (safePage - 1) * histPageSize,
      safePage * histPageSize,
    );
  }, [sortedHistory, histPage, histPageSize]);

  function onSortSettlements(nextKey: SettlementSortKey) {
    setStlSortDirection((prev) =>
      stlSortKey === nextKey ? (prev === "asc" ? "desc" : "asc") : "asc",
    );
    setStlSortKey(nextKey);
    setStlPage(1);
  }

  function onSortHistory(nextKey: ClaimHistorySortKey) {
    setHistSortDirection((prev) =>
      histSortKey === nextKey ? (prev === "asc" ? "desc" : "asc") : "desc",
    );
    setHistSortKey(nextKey);
    setHistPage(1);
  }

  const settlementColumns = useMemo<
    DataTableColumn<Settlement, SettlementSortKey>[]
  >(
    () => [
      {
        id: "accountId",
        label: "Account",
        sortable: true,
        sortKey: "accountId",
        renderCell: (settlement) => (
          <span className="font-mono text-xs">{settlement.accountId}</span>
        ),
      },
      {
        id: "token",
        label: "Token",
        sortable: true,
        sortKey: "token",
        renderCell: (settlement) => (
          <TokenBadge
            token={settlement.token}
            colorClass={settlement.tokenColor}
          />
        ),
      },
      {
        id: "amount",
        label: "Amount",
        align: "right",
        sortable: true,
        sortKey: "amount",
        renderCell: (settlement) => (
          <span className="font-medium text-xs">
            {formatAmount(settlement.amount)}
          </span>
        ),
      },
      {
        id: "estimatedValue",
        label: "Est. Value",
        align: "right",
        sortable: true,
        sortKey: "estimatedValue",
        renderCell: (settlement) => (
          <span className="text-xs">
            {formatCurrency(settlement.estimatedValue)}
          </span>
        ),
      },
      {
        id: "daysRemaining",
        label: "Claim Deadline",
        align: "center",
        sortable: true,
        sortKey: "daysRemaining",
        renderCell: (settlement) => (
          <div className="text-center">
            <div className="text-xs">{settlement.claimDeadline}</div>
            <div
              className={`text-xs ${settlement.daysRemaining <= 3 ? "text-ascend-warning font-medium" : "text-muted-foreground"}`}
            >
              {settlement.daysRemaining === 0
                ? "Expired"
                : `${settlement.daysRemaining} day${settlement.daysRemaining === 1 ? "" : "s"} left`}
            </div>
          </div>
        ),
      },
      {
        id: "status",
        label: "Status",
        align: "center",
        sortable: true,
        sortKey: "status",
        renderCell: (settlement) => (
          <div className="flex justify-center">
            <SettlementStatusBadge status={settlement.status} />
          </div>
        ),
      },
      {
        id: "action",
        label: "Action",
        align: "center",
        renderCell: (settlement) => (
          <div className="flex justify-center">
            {settlement.status !== "expired" ? (
              <Button
                size="sm"
                disabled={claimingId === settlement.id || isClaimingAll}
                onClick={() => handleClaim(settlement.id)}
              >
                {claimingId === settlement.id ? (
                  <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 animate-spin" />
                ) : (
                  "Claim"
                )}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            )}
          </div>
        ),
      },
    ],
    [claimingId, isClaimingAll],
  );

  const claimHistoryColumns = useMemo<
    DataTableColumn<ClaimRecord, ClaimHistorySortKey>[]
  >(
    () => [
      {
        id: "date",
        label: "Date",
        sortable: true,
        sortKey: "date",
        renderCell: (record) => (
          <span className="text-muted-foreground text-xs">{record.date}</span>
        ),
      },
      {
        id: "accountId",
        label: "Account",
        sortable: true,
        sortKey: "accountId",
        renderCell: (record) => (
          <span className="font-mono text-xs">{record.accountId}</span>
        ),
      },
      {
        id: "token",
        label: "Token",
        sortable: true,
        sortKey: "token",
        renderCell: (record) => (
          <TokenBadge token={record.token} colorClass={record.tokenColor} />
        ),
      },
      {
        id: "amount",
        label: "Amount",
        align: "right",
        sortable: true,
        sortKey: "amount",
        renderCell: (record) => (
          <span className="font-medium text-xs">
            {formatAmount(record.amount)}
          </span>
        ),
      },
      {
        id: "valueUsd",
        label: "Value",
        align: "right",
        sortable: true,
        sortKey: "valueUsd",
        renderCell: (record) => (
          <span className="text-xs text-muted-foreground">
            {formatCurrency(record.valueUsd)}
          </span>
        ),
      },
      {
        id: "status",
        label: "Status",
        align: "center",
        sortable: true,
        sortKey: "status",
        renderCell: (record) => (
          <div className="flex justify-center">
            <ClaimHistoryStatusBadge status={record.status} />
          </div>
        ),
      },
    ],
    [],
  );

  function handleClaim(id: string) {
    setClaimingId(id);
    setTimeout(() => {
      setSettlements((prev) => prev.filter((s) => s.id !== id));
      setClaimingId(null);
      toast.success("Claim submitted successfully!");
    }, 1500);
  }

  function handleClaimAll() {
    setIsClaimingAll(true);
    setTimeout(() => {
      const claimableIds = claimableSettlements.map((s) => s.id);
      setSettlements((prev) =>
        prev.filter((s) => !claimableIds.includes(s.id)),
      );
      setIsClaimingAll(false);
      toast.success(`${claimableIds.length} settlements claimed successfully!`);
    }, 2500);
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <button
              onClick={() => navigate("/liquidations")}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-1"
            >
              <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-3 w-3" />
              Back to Liquidations
            </button>
            <h1 className="text-xl sm:text-2xl font-bold">Claim Settlements</h1>
            <p className="text-sm text-muted-foreground">
              For{" "}
              <span className="font-medium text-foreground">
                verified buyers
              </span>{" "}
              only: pull collateral or proceeds after you
              <span className="font-medium text-foreground">
                {" "}
                won a DDF auction
              </span>{" "}
              or{" "}
              <span className="font-medium text-foreground">
                directly liquidated a margin account
              </span>{" "}
              on a credit facility where you passed facility compliance.
            </p>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Claimable Settlements"
          value={claimableSettlements.length.toString()}
          subtitle="Ready to claim"
          icon={<Icon name="streamline:gift" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Claimable Value"
          value={formatCurrency(totalClaimableValue)}
          subtitle="Estimated across all settlements"
          icon={<Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Claimed This Month"
          value={formatCurrency(claimedThisMonth)}
          subtitle="March 2026"
          icon={<Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-5 w-5 text-ascend-success" />}
        />
        <StatCard
          title="Expiring Soon"
          value={expiringSoon.toString()}
          subtitle="Within 3 days"
          icon={<Icon name="streamline:blank-calendar" className="h-5 w-5 text-ascend-warning" />}
          valueClassName={expiringSoon > 0 ? "text-ascend-warning" : ""}
        />
      </div>

      {/* Claimable Settlements */}
      <Card>
        <CardHeader className="p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Claimable Settlements</CardTitle>
              <CardDescription>
                {filteredSettlements.length} of {settlements.length} - auction
                wins and direct liquidations awaiting your claim
              </CardDescription>
            </div>
            {claimableSettlements.length > 0 && (
              <Button
                onClick={handleClaimAll}
                disabled={isClaimingAll || claimableSettlements.length === 0}
              >
                {isClaimingAll ? (
                  <>
                    <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />
                    Claiming All...
                  </>
                ) : (
                  <>
                    <Icon name="streamline:shipping-box-1" className="mr-2 h-4 w-4" />
                    Claim All ({claimableSettlements.length})
                  </>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6 pt-0 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <FilterTabs<SettlementFilter>
              filters={SETTLEMENT_FILTERS}
              active={stlStatusFilter}
              onChange={(v) => {
                setStlStatusFilter(v);
                setStlPage(1);
              }}
              counts={stlStatusCounts}
            />
            <SearchBar
              value={stlSearch}
              onChange={(v) => {
                setStlSearch(v);
                setStlPage(1);
              }}
              placeholder="Search by account or token..."
              className="w-full sm:w-64"
            />
          </div>

          {settlements.filter((s) => s.status === "expiring-soon").length >
            0 && (
            <Alert>
              <Icon name="streamline:warning-triangle" className="h-4 w-4" />
              <AlertDescription>
                {settlements.filter((s) => s.status === "expiring-soon").length}{" "}
                settlement(s) expiring within 3 days. Claim them before the
                deadline to avoid losing collateral.
              </AlertDescription>
            </Alert>
          )}

          {filteredSettlements.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              No settlements match your filters.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <DataTable<Settlement, SettlementSortKey>
                  columns={settlementColumns}
                  rows={paginatedSettlements}
                  rowKey={(settlement) => settlement.id}
                  sort={{
                    key: stlSortKey,
                    direction: stlSortDirection,
                    onSort: onSortSettlements,
                  }}
                  rowClassName={(settlement) =>
                    settlement.status === "expiring-soon"
                      ? "bg-ascend-warning/5"
                      : ""
                  }
                  emptyMessage="No settlements match your filters."
                />
              </div>

              {/* Mobile stacked cards */}
              <div className="md:hidden space-y-3">
                {paginatedSettlements.map((stl) => (
                  <div
                    key={stl.id}
                    className={`rounded-lg border p-4 space-y-3 ${
                      stl.status === "expiring-soon"
                        ? "border-ascend-warning/40 bg-ascend-warning/5"
                        : "border-border"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">
                          {stl.accountId}
                        </span>
                        <TokenBadge
                          token={stl.token}
                          colorClass={stl.tokenColor}
                        />
                      </div>
                      <SettlementStatusBadge status={stl.status} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Amount</span>
                        <div className="font-medium">
                          {formatAmount(stl.amount)} {stl.token}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-muted-foreground">
                          Est. Value
                        </span>
                        <div className="font-medium">
                          {formatCurrency(stl.estimatedValue)}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Deadline</span>
                        <div className="font-medium">{stl.claimDeadline}</div>
                      </div>
                      <div className="text-right">
                        <span className="text-muted-foreground">Countdown</span>
                        <div
                          className={`font-medium ${stl.daysRemaining <= 3 ? "text-ascend-warning" : ""}`}
                        >
                          {stl.daysRemaining === 0
                            ? "Expired"
                            : `${stl.daysRemaining}d remaining`}
                        </div>
                      </div>
                    </div>
                    {stl.status !== "expired" && (
                      <Button
                        size="sm"
                        className="w-full"
                        disabled={claimingId === stl.id || isClaimingAll}
                        onClick={() => handleClaim(stl.id)}
                      >
                        {claimingId === stl.id ? (
                          <>
                            <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />
                            Claiming...
                          </>
                        ) : (
                          "Claim"
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <PaginationControls
            page={stlPage}
            setPage={setStlPage}
            pageSize={stlPageSize}
            setPageSize={setStlPageSize}
            totalItems={filteredSettlements.length}
            label="settlements"
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          />
        </CardContent>
      </Card>

      {/* Claim History */}
      <Card>
        <CardHeader className="p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Claim History</CardTitle>
              <CardDescription>
                Past verified-buyer claims from auctions or direct facility
                liquidations.
              </CardDescription>
            </div>
            <SearchBar
              value={histSearch}
              onChange={(v) => {
                setHistSearch(v);
                setHistPage(1);
              }}
              placeholder="Search history..."
              className="w-full sm:w-64"
            />
          </div>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          {/* Desktop table */}
          <div className="hidden md:block">
            <DataTable<ClaimRecord, ClaimHistorySortKey>
              columns={claimHistoryColumns}
              rows={paginatedHistory}
              rowKey={(record) => record.id}
              sort={{
                key: histSortKey,
                direction: histSortDirection,
                onSort: onSortHistory,
              }}
              emptyMessage="No claim history records match your search."
            />
          </div>

          {/* Mobile stacked cards */}
          <div className="md:hidden space-y-3">
            {paginatedHistory.map((rec) => (
              <div
                key={rec.id}
                className="rounded-lg border border-border p-4 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{rec.accountId}</span>
                    <TokenBadge token={rec.token} colorClass={rec.tokenColor} />
                  </div>
                  <ClaimHistoryStatusBadge status={rec.status} />
                </div>
                <div className="text-xs text-muted-foreground">{rec.date}</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Amount</span>
                    <div className="font-medium">
                      {formatAmount(rec.amount)} {rec.token}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-muted-foreground">Value</span>
                    <div className="font-medium">
                      {formatCurrency(rec.valueUsd)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <PaginationControls
            page={histPage}
            setPage={setHistPage}
            pageSize={histPageSize}
            setPageSize={setHistPageSize}
            totalItems={filteredHistory.length}
            label="records"
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          />
        </CardContent>
      </Card>
    </div>
  );
}
